/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import moment from "moment";
import Button from "../../../components/Button";
import { strings } from "../../../common/Strings/Strings";
import { ChevronLeft } from "../../../common/Icons/ChevronLeft";
import { ChevronRight } from "../../../common/Icons/ChevronRight";
import SimpleCheckBox from "../../../components/InputFieldsSimple/SimpleCheckBox";
import SimpleSelect from "../../../components/InputFieldsSimple/SimpleSelect";
import MonthCalendarNavigation from "./MonthCalendarNavigation";
import Tag, { TagType } from "../../../components/Tag";
import { ViewTypes } from "../MainCalendar";
import AddDropdown from "./AddDropdown";
import { momentDateFormat, navigationPeriodInDays } from "../calendarConfig";

interface Props {
  selectedDay: string;
  setSelectedDay(newDay: string): void;
  setWeekEnd(value: boolean): void;
  setViewType(value: ViewTypes): void;

  openNewReservationModal(): void;
  openNewShiftModal(): void;
  setIsEmergency(value: boolean): void;
  viewType: ViewTypes | undefined;
  weekEnd: boolean;
}

const Navigation: React.FC<Props> = ({
  selectedDay,
  setSelectedDay,
  setWeekEnd,
  setViewType,
  openNewReservationModal,
  openNewShiftModal,
  setIsEmergency,
  viewType,
  weekEnd
}: Props) => {
  const [realFirstDay, setRealFirstDay] = useState<string>(moment().format());
  const [realLastDay, setRealLastDay] = useState<moment.Moment>(moment());
  useEffect(() => {
    /* First week is the week of today */
    if (!moment(selectedDay).isBetween(realFirstDay, realLastDay, "days", "(]")) {
      const firstDay = moment(selectedDay).weekday(0);
      setRealFirstDay(firstDay.format());
      setRealLastDay(firstDay.add(navigationPeriodInDays - 1, "days"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  const next = (where: string, days: number): void => {
    let nextDate = moment(new Date()).format(momentDateFormat);

    if (where === "left") {
      nextDate = moment(selectedDay).weekday(0).subtract(days, "d").format(momentDateFormat);
    }

    if (where === "right") {
      nextDate = moment(selectedDay).weekday(0).add(days, "d").format(momentDateFormat);
    }
    setSelectedDay(nextDate);
  };


  const onChange = (view: string) => {
    switch (view) {
      case ViewTypes.DAY:
        setViewType(ViewTypes.DAY);
        break;
      case ViewTypes.DOCTOR_DAY:
        setViewType(ViewTypes.DOCTOR_DAY);
        break;
      case ViewTypes.WEEK:
        setViewType(ViewTypes.WEEK);
        break;
      case ViewTypes.SHIFTLISTVIEW:
        setViewType(ViewTypes.SHIFTLISTVIEW);
        break;
      case ViewTypes.YEAR:
        setViewType(ViewTypes.YEAR);
        break;
      default:
        setViewType(ViewTypes.LISTVIEW);
        break;
    }
  };
  return (
    <>
      <div className="flex flex-nowrap lg:flex-nowrap lg:grid lg:grid-cols-3 justify-between items-center">
        <div className="flex items-center space-x-4 justify-start">
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                setSelectedDay(moment(new Date()).format(momentDateFormat));
              }}
            >
              <span className="tw-link-tercier">{strings.today}</span>
            </button>
          </div>
          <div className="hidden lg:flex space-x-4">
            <button
              type="button"
              className="tw-icon tw-link"
              onClick={() => {
                next(
                  "left",
                  moment(selectedDay).diff(moment(selectedDay).subtract(navigationPeriodInDays, "days"), "days")
                );
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="tw-icon tw-link"
              onClick={() => {
                next(
                  "right",
                  0 - moment(selectedDay).diff(moment(selectedDay).add(navigationPeriodInDays, "days"), "days")
                );
              }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <div className="hidden lg:flex space-x-2">
            <span className="text-lg font-normal text-zinc-800 dark:text-gray-300">
              {moment(realFirstDay).format("LL")}
            </span>
            <span>-</span>
            <span className="text-lg font-normal text-zinc-800 dark:text-gray-300">
              {moment(realLastDay).format("LL")}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-start lg:justify-center">
          <h1 className="hidden lg:flex text-sm font-medium leading-tight text-zinc-800 lg:text-2xl dark:text-white">
            {moment(selectedDay).format("LL")}
            {` (${moment(selectedDay).week()}. ${strings.week})`}
          </h1>
        </div>
        <div className="flex items-center space-x-4 justify-end">
          <div className="hidden lg:flex">
            <SimpleCheckBox
              checked={weekEnd}
              label={strings.weekend}
              name="weekend"
              onChange={(checked: boolean) => {
                setWeekEnd(checked);
              }}
            />
          </div>
           <div className="flex">
                <div className="calendar-views-desktop hidden md:flex">
                  <SimpleSelect
                    name="view"
                    isChooseOption={false}
                    labelOff
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(e.target.value);
                    }}
                    options={[
                      {
                        title: `${strings.Day} (${strings.reservations})\xa0\xa0\xa0\xa0\xa0\xa0`,
                        value: ViewTypes.DAY,
                      },
                      {
                        title: `${strings.listView} (${strings.reservations})\xa0\xa0\xa0\xa0\xa0\xa0`,
                        value: ViewTypes.LISTVIEW,
                      },
                      {
                        title: `${strings.doctorView} (${strings.medicalRecords})\xa0\xa0\xa0\xa0\xa0\xa0`,
                        value: ViewTypes.DOCTOR_DAY,
                      },
                      {
                        title: `${strings.Week} (${strings.shifts})`,
                        value: ViewTypes.WEEK,
                      },
                      {
                        title: `${strings.listView} (${strings.shifts})`,
                        value: ViewTypes.SHIFTLISTVIEW,
                      },
                      {
                        title: strings.Year,
                        value: ViewTypes.YEAR,
                      },
                    ]}
                    value={viewType}
                  />
                </div>
              </div>

          <AddDropdown
            openNewReservationModal={openNewReservationModal}
            openNewShiftModal={openNewShiftModal}
            setIsEmergency={setIsEmergency}
          />
        </div>
      </div>
      <div id="navigation-second-row">
        <MonthCalendarNavigation
          localSelectedDay={selectedDay}
          realFirstDay={realFirstDay}
          realLastDay={realLastDay}
          setLocalSelectedDay={(newDate: string) => {
            setSelectedDay(newDate);
          }}
          viewType={viewType}
        />
      </div>
    </>
  );
};

export default Navigation;
